import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<string | null>(null);
  const location = useLocation();
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';

  const navItems = [
    { label: 'Services', href: '/services' },
    { label: 'Industries', href: '/industries' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Case Studies', href: '/case-studies' },
    { label: 'How We Work', href: '/how-we-work' },
    { label: 'About', href: '/about' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown-container]')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const handleDropdownEnter = (name: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 300);
  };

  const handleDropdownClick = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(null);
  };

  const toggleMobileAccordion = (name: string) => {
    setOpenMobileAccordion(openMobileAccordion === name ? null : name);
  };

  // Determine if header should have solid background
  const hasSolidBackground = !isLandingPage || isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        hasSolidBackground ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-plant-line text-3xl text-emerald-600"></i>
            </div>
            <div className="flex flex-col">
              <span className={`text-xl font-bold transition-colors leading-tight ${hasSolidBackground ? 'text-slate-900' : 'text-white'}`}>
                Greenfield Partners
              </span>
              <span className={`text-xs transition-colors ${hasSolidBackground ? 'text-slate-500' : 'text-white/80'}`}>
                Precision Drone Services
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {/* Services Dropdown */}
            <div
              className="relative"
              data-dropdown-container
              onMouseEnter={() => handleDropdownEnter('services')}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 font-medium transition-colors ${
                  hasSolidBackground ? 'text-slate-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'
                }`}
              >
                Services
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${openDropdown === 'services' ? 'rotate-180' : ''}`}></i>
              </button>
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ease-out ${
                openDropdown === 'services'
                  ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
              }`}>
                <div className="w-56 bg-white rounded-lg shadow-lg py-2 border border-slate-200 max-h-[80vh] overflow-y-auto">
                  <Link
                    to="/services"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    All Services
                  </Link>
                  <Link
                    to="/services/agriculture"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Agriculture
                  </Link>
                  <Link
                    to="/services/timber-forestry"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Timber & Forestry
                  </Link>
                  <Link
                    to="/services/land-intelligence"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Land Intelligence & Due Diligence
                  </Link>
                  <Link
                    to="/services/portfolio-monitoring"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Portfolio Monitoring & Asset Oversight
                  </Link>
                  <Link
                    to="/services/deer-deterrent"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Deer Deterrent
                  </Link>
                  <Link
                    to="/services/surveys"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Surveys
                  </Link>
                  <Link
                    to="/services/recovery"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Recovery Services
                  </Link>
                  <Link
                    to="/infrastructure-pricing"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Infrastructure Pricing
                  </Link>
                </div>
              </div>
            </div>

            {/* Tools Dropdown */}
            <div
              className="relative"
              data-dropdown-container
              onMouseEnter={() => handleDropdownEnter('tools')}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 font-medium transition-colors ${
                  hasSolidBackground ? 'text-slate-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'
                }`}
              >
                Tools
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${openDropdown === 'tools' ? 'rotate-180' : ''}`}></i>
              </button>
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ease-out ${
                openDropdown === 'tools'
                  ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
              }`}>
                <div className="w-56 bg-white rounded-lg shadow-lg py-2 border border-slate-200 max-h-[80vh] overflow-y-auto">
                  <Link
                    to="/instant-quote"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Instant Quote
                  </Link>
                  <Link
                    to="/roi-calculator"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    ROI Calculator
                  </Link>
                  <Link
                    to="/spray-window-advisor"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Spray Window Advisor
                  </Link>
                  <Link
                    to="/crop-calendar"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Crop Calendar
                  </Link>
                  <Link
                    to="/operator-checklist"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Operator Checklist
                  </Link>
                  <Link
                    to="/field-complexity"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Field Complexity Estimator
                  </Link>
                </div>
              </div>
            </div>

            {/* Resources Dropdown */}
            <div
              className="relative"
              data-dropdown-container
              onMouseEnter={() => handleDropdownEnter('resources')}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 font-medium transition-colors ${
                  hasSolidBackground ? 'text-slate-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'
                }`}
              >
                Resources
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${openDropdown === 'resources' ? 'rotate-180' : ''}`}></i>
              </button>
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ease-out ${
                openDropdown === 'resources'
                  ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
              }`}>
                <div className="w-56 bg-white rounded-lg shadow-lg py-2 border border-slate-200 max-h-[80vh] overflow-y-auto">
                  <Link
                    to="/case-studies"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Case Studies
                  </Link>
                  <Link
                    to="/seasonal-programs"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Seasonal Programs
                  </Link>
                  <Link
                    to="/blog/drone-maps-precision-agriculture"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Drone Mapping Guide
                  </Link>
                  <Link
                    to="/drone-spraying-problems"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    When Spraying Goes Wrong
                  </Link>
                  <Link
                    to="/blog/deer-damage-cost"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Deer Damage Blog
                  </Link>
                  <Link
                    to="/faq"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    FAQ
                  </Link>
                  <Link
                    to="/financing"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Financing
                  </Link>
                </div>
              </div>
            </div>

            {/* Company Dropdown */}
            <div
              className="relative"
              data-dropdown-container
              onMouseEnter={() => handleDropdownEnter('company')}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 font-medium transition-colors ${
                  hasSolidBackground ? 'text-slate-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'
                }`}
              >
                Company
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${openDropdown === 'company' ? 'rotate-180' : ''}`}></i>
              </button>
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ease-out ${
                openDropdown === 'company'
                  ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
              }`}>
                <div className="w-56 bg-white rounded-lg shadow-lg py-2 border border-slate-200 max-h-[80vh] overflow-y-auto">
                  <Link
                    to="/about"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    About Us
                  </Link>
                  <Link
                    to="/how-we-work"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    How We Work
                  </Link>
                  <Link
                    to="/industries"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Industries
                  </Link>
                  <Link
                    to="/careers"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Careers
                  </Link>
                  <Link
                    to="/contact"
                    onClick={handleDropdownClick}
                    className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/instant-quote"
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              Get Quote
            </Link>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              Contact Us
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center ${
              hasSolidBackground ? 'text-slate-900' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {/* Services Accordion */}
            <div className="border-b border-slate-200">
              <button
                onClick={() => toggleMobileAccordion('services')}
                className="w-full flex items-center justify-between py-3 text-slate-900 font-medium"
              >
                Services
                <i className={`ri-arrow-${openMobileAccordion === 'services' ? 'up' : 'down'}-s-line`}></i>
              </button>
              {openMobileAccordion === 'services' && (
                <div className="pb-3 pl-4 space-y-2">
                  <Link to="/services" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    All Services
                  </Link>
                  <Link to="/services/agriculture" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Agriculture
                  </Link>
                  <Link to="/services/timber-forestry" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Timber & Forestry
                  </Link>
                  <Link to="/services/land-intelligence" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Land Intelligence & Due Diligence
                  </Link>
                  <Link to="/services/portfolio-monitoring" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Portfolio Monitoring & Asset Oversight
                  </Link>
                  <Link to="/services/deer-deterrent" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Deer Deterrent
                  </Link>
                  <Link to="/services/surveys" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Surveys
                  </Link>
                  <Link to="/services/recovery" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Recovery Services
                  </Link>
                  <Link to="/infrastructure-pricing" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Infrastructure Pricing
                  </Link>
                </div>
              )}
            </div>

            {/* Tools Accordion */}
            <div className="border-b border-slate-200">
              <button
                onClick={() => toggleMobileAccordion('tools')}
                className="w-full flex items-center justify-between py-3 text-slate-900 font-medium"
              >
                Tools
                <i className={`ri-arrow-${openMobileAccordion === 'tools' ? 'up' : 'down'}-s-line`}></i>
              </button>
              {openMobileAccordion === 'tools' && (
                <div className="pb-3 pl-4 space-y-2">
                  <Link to="/instant-quote" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Instant Quote
                  </Link>
                  <Link to="/roi-calculator" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    ROI Calculator
                  </Link>
                  <Link to="/spray-window-advisor" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Spray Window Advisor
                  </Link>
                  <Link to="/crop-calendar" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Crop Calendar
                  </Link>
                  <Link to="/operator-checklist" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Operator Checklist
                  </Link>
                  <Link to="/field-complexity" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Field Complexity Estimator
                  </Link>
                </div>
              )}
            </div>

            {/* Resources Accordion */}
            <div className="border-b border-slate-200">
              <button
                onClick={() => toggleMobileAccordion('resources')}
                className="w-full flex items-center justify-between py-3 text-slate-900 font-medium"
              >
                Resources
                <i className={`ri-arrow-${openMobileAccordion === 'resources' ? 'up' : 'down'}-s-line`}></i>
              </button>
              {openMobileAccordion === 'resources' && (
                <div className="pb-3 pl-4 space-y-2">
                  <Link to="/case-studies" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Case Studies
                  </Link>
                  <Link to="/seasonal-programs" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Seasonal Programs
                  </Link>
                  <Link to="/blog/drone-maps-precision-agriculture" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Drone Mapping Guide
                  </Link>
                  <Link to="/drone-spraying-problems" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    When Spraying Goes Wrong
                  </Link>
                  <Link to="/blog/deer-damage-cost" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Deer Damage Blog
                  </Link>
                  <Link to="/faq" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    FAQ
                  </Link>
                  <Link to="/financing" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Financing
                  </Link>
                </div>
              )}
            </div>

            {/* Company Accordion */}
            <div className="border-b border-slate-200">
              <button
                onClick={() => toggleMobileAccordion('company')}
                className="w-full flex items-center justify-between py-3 text-slate-900 font-medium"
              >
                Company
                <i className={`ri-arrow-${openMobileAccordion === 'company' ? 'up' : 'down'}-s-line`}></i>
              </button>
              {openMobileAccordion === 'company' && (
                <div className="pb-3 pl-4 space-y-2">
                  <Link to="/about" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    About Us
                  </Link>
                  <Link to="/how-we-work" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    How We Work
                  </Link>
                  <Link to="/industries" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Industries
                  </Link>
                  <Link to="/careers" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Careers
                  </Link>
                  <Link to="/contact" className="block py-2 text-sm text-slate-600 hover:text-emerald-600">
                    Contact Us
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/instant-quote"
              className="block w-full text-center px-6 py-3 mt-4 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Get Quote
            </Link>
            <Link
              to="/contact"
              className="block w-full text-center px-6 py-3 mt-4 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
